/**
 * FloatingSupportButton - Fixed support button with popup card
 * Rendered via portal to document.body so it stays visible above all layouts.
 * Only shown on Home, Features, Contact, and Pricing pages—not on login, register, or other auth pages.
 */

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { Headphones, Mail, Phone, X } from 'lucide-react'
import { isMarketingLandingRoute } from '@/utils/route.util'

const SUPPORT_EMAIL = 'support@vidhyantra.com'
const SUPPORT_PHONE = '+91 964-974-9845'
const WHATSAPP_NUMBER = '919649749845'

export function FloatingSupportButton() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        open &&
        popupRef.current &&
        buttonRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  if (!isMarketingLandingRoute(location.pathname)) return null

  const content = (
    <div className="fixed bottom-6 right-6 z-[9999]">
      {open && (
        <div
          ref={popupRef}
          className="absolute bottom-16 right-0 w-72 rounded-xl bg-card shadow-xl border border-[#E2E8F0] p-4 animate-in fade-in slide-in-from-bottom-2 duration-200"
          role="dialog"
          aria-label="Contact support"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-[#0F172A]">Contact Support</h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3 mb-4">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="flex items-center gap-2 text-sm text-primary !no-underline transition-colors hover:brightness-90 hover:!no-underline"
            >
              <Mail className="w-4 h-4 flex-shrink-0 text-primary" />
              {SUPPORT_EMAIL}
            </a>
            <a
              href={`tel:${SUPPORT_PHONE.replace(/\s/g, '')}`}
              className="flex items-center gap-2 text-sm text-primary !no-underline transition-colors hover:brightness-90 hover:!no-underline"
            >
              <Phone className="w-4 h-4 flex-shrink-0 text-primary" />
              {SUPPORT_PHONE}
            </a>
          </div>
          <div className="space-y-2">
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="block w-full rounded-lg bg-primary px-4 py-2.5 text-center text-sm font-semibold text-primary-foreground !no-underline transition-colors hover:brightness-90 hover:!text-primary-foreground hover:!no-underline"
            >
              Book Demo
            </Link>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="block w-full rounded-lg border border-border px-4 py-2.5 text-center text-sm font-medium text-primary !no-underline transition-colors hover:border-primary/40 hover:bg-primary/5 hover:brightness-90 hover:!no-underline"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
        aria-label="Contact support"
        aria-expanded={open}
      >
        <Headphones className="w-6 h-6" />
      </button>
    </div>
  )

  return typeof document !== 'undefined'
    ? createPortal(content, document.body)
    : content
}

export default FloatingSupportButton
